import React, { useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Code,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Copy, Check } from 'phosphor-react';
import type { ITokenGerado } from '../types';

interface Props {
  token: ITokenGerado | null;
  onClose: () => void;
}

/**
 * Exibe a credencial recém-criada.
 *
 * Sem botão de fechar no canto e sem fechar por ESC/clique-fora de propósito: o
 * token não é recuperável, e fechar sem copiar significa gerar outro e revogar
 * este. O usuário precisa confirmar explicitamente que copiou.
 */
export const TokenGeradoModal: React.FC<Props> = ({ token, onClose }) => {
  const [copiado, setCopiado] = useState(false);
  const toast = useToast();

  if (!token) return null;

  const linhaEnv = `MONITORAMENTO_TOKEN=${token.token}`;

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(linhaEnv);
      setCopiado(true);
      toast({ title: 'Copiado', status: 'success', duration: 2000 });
    } catch {
      // clipboard bloqueado (http, permissão): o textarea abaixo continua servindo
      toast({
        title: 'Não consegui copiar automaticamente',
        description: 'Selecione o texto e copie manualmente.',
        status: 'warning',
      });
    }
  };

  const fechar = () => {
    setCopiado(false);
    onClose();
  };

  return (
    <Modal isOpen onClose={fechar} size="xl" closeOnOverlayClick={false} closeOnEsc={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Credencial de {token.bot_name}</ModalHeader>
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                Este token <strong>não pode ser exibido de novo</strong>. Guardamos só o hash. Se
                perder, gere outro e revogue este.
              </Text>
            </Alert>

            <Box>
              <Text fontSize="sm" mb={2} color="gray.600">
                Cole no <Code fontSize="xs">.env</Code> do bot:
              </Text>
              <Textarea
                value={linhaEnv}
                isReadOnly
                fontFamily="mono"
                fontSize="xs"
                rows={3}
                onFocus={e => e.target.select()}
                bg="gray.50"
              />
            </Box>

            <HStack fontSize="xs" color="gray.500" spacing={4}>
              <Text>
                Identificador: <Code fontSize="xs">{token.prefixo}…</Code>
              </Text>
              <Text>É por ele que a credencial aparece na lista.</Text>
            </HStack>
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button
            leftIcon={copiado ? <Check size={16} /> : <Copy size={16} />}
            onClick={copiar}
            colorScheme={copiado ? 'green' : 'blue'}
            variant={copiado ? 'outline' : 'solid'}
          >
            {copiado ? 'Copiado' : 'Copiar'}
          </Button>
          <Button onClick={fechar} isDisabled={!copiado} variant="ghost">
            {copiado ? 'Fechar' : 'Copie antes de fechar'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
